import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { createScheduleBlock, updateScheduleBlock, deleteScheduleBlock } from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const BLANK = { time: "", label: "", hint: "" };

export default function ScheduleBlockDialog({ open, onOpenChange, block, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(block);

  useEffect(() => {
    if (open) {
      setForm(block ? { time: block.time, label: block.label, hint: block.hint || "" } : BLANK);
    }
  }, [open, block]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.time.trim() || !form.label.trim()) {
      toast.error("Time and label are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateScheduleBlock(block.id, form);
        toast.success("Block updated");
      } else {
        await createScheduleBlock(form);
        toast.success("Block added");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch {
      toast.error("Could not save block");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await deleteScheduleBlock(block.id);
      toast.success("Block removed");
      onOpenChange(false);
      onSaved && onSaved();
    } catch {
      toast.error("Could not delete block");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border text-foreground max-w-md" data-testid="schedule-block-dialog">
        <DialogHeader>
          <DialogTitle className="mono tracking-tight">
            $ {isEdit ? "edit block" : "new block"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="overline block mb-2">Time</label>
            <Input
              data-testid="schedule-block-time-input"
              placeholder="09:00 – 10:00"
              value={form.time}
              onChange={(e) => update("time", e.target.value)}
              className="bg-muted border-border mono"
            />
          </div>
          <div>
            <label className="overline block mb-2">Label</label>
            <Input
              data-testid="schedule-block-label-input"
              placeholder="Deep Work"
              value={form.label}
              onChange={(e) => update("label", e.target.value)}
              className="bg-muted border-border"
            />
          </div>
          <div>
            <label className="overline block mb-2">Hint</label>
            <Textarea
              data-testid="schedule-block-hint-input"
              placeholder="What to actually do in this block."
              value={form.hint}
              onChange={(e) => update("hint", e.target.value)}
              className="bg-muted border-border resize-none"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter className="flex items-center sm:justify-between gap-2">
          {isEdit ? (
            <Button
              variant="outline"
              data-testid="schedule-block-delete-btn"
              onClick={remove}
              disabled={saving}
              className="border-destructive text-destructive hover:bg-destructive hover:text-white mono text-xs"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          ) : <span />}
          <Button
            data-testid="schedule-block-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-secondary hover:text-white mono uppercase tracking-widest text-xs"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
